import { Amount, asAmount, IssuerId } from "@goodfill/domain-properties";
import { formatCouponPayment, formatMaturityDate } from "@goodfill/domain-properties-components";
import Decimal from "decimal.js";
import { AccountBondHolding } from "../../../AccountHolding";
import { PortfolioDistribution, PortfolioPart } from "./PortfolioDistribution";
import { TIMEZONE_NEY_YORK } from "../../../../../locale/timezone";

export const portfolioDistributionGroupByValues = ["bond", "issuer", "ticker", "industry"] as const;
export type PortfolioDistributionGroupBy = typeof portfolioDistributionGroupByValues[number];

type GroutByMap = {
  [Property in PortfolioDistributionGroupBy]: (
    bondHoldings: AccountBondHolding[],
    totalAmount: Decimal,
  ) => PortfolioDistribution;
};

const byBond = (bondHoldings: AccountBondHolding[], totalAmount: Decimal): PortfolioDistribution =>
  bondHoldings.map((bondHoldings): PortfolioPart => {
    const absolute = bondHoldings.bondBalanceAccount.balance;
    const percentage = bondHoldings.bondBalanceAccount.balance.div(totalAmount);

    const bond = bondHoldings.pricedBond;

    const formattedCoupon = formatCouponPayment({ value: bond.couponPayment });
    const formattedMaturity = formatMaturityDate({
      value: bond.maturityDate,
      timezone: TIMEZONE_NEY_YORK,
    });
    const labelString = `${bond.issuerName} ${formattedCoupon} ${formattedMaturity}`;

    return {
      id: `${bond.bondIdCusip}`,
      labelString: labelString,
      label: labelString,
      absolute,
      percentage,
    };
  });

interface IssuerGroup {
  issuerId: IssuerId;
  issuerName: string;
  balance: Amount;
}

const byIssuer = (bondHoldings: AccountBondHolding[], totalAmount: Decimal): PortfolioDistribution =>
  Array.from(
    bondHoldings
      .reduce((acc, bondHoldings) => {
        const pricedBond = bondHoldings.pricedBond;
        const item = acc.get(pricedBond.issuerId) ?? {
          issuerId: pricedBond.issuerId,
          issuerName: pricedBond.issuerName,
          balance: asAmount(new Decimal(0)),
        };
        acc.set(pricedBond.issuerId, {
          ...item,
          balance: asAmount(item.balance.plus(bondHoldings.bondBalanceAccount.balance)),
        });

        return acc;
      }, new Map<IssuerId, IssuerGroup>())
      .values(),
  ).map((group: IssuerGroup): PortfolioPart => {
    const absolute = group.balance;
    const percentage = absolute.div(totalAmount);

    return {
      id: `${group.issuerId}`,
      label: group.issuerName,
      labelString: group.issuerName,
      absolute,
      percentage,
    };
  });

interface TickerGroup {
  ticker: string;
  balance: Amount;
}

const byTicker = (bondHoldings: AccountBondHolding[], totalAmount: Decimal): PortfolioDistribution =>
  Array.from(
    bondHoldings
      .reduce((acc, bondHoldings) => {
        const ticker = bondHoldings.pricedBond.issuerTicker;
        const item = acc.get(ticker) ?? { ticker, balance: asAmount(new Decimal(0)) };
        acc.set(ticker, { ...item, balance: asAmount(item.balance.plus(bondHoldings.bondBalanceAccount.balance)) });

        return acc;
      }, new Map<string, TickerGroup>())
      .values(),
  ).map((group: TickerGroup): PortfolioPart => {
    const absolute = group.balance;
    const percentage = absolute.div(totalAmount);

    return {
      id: group.ticker,
      label: group.ticker,
      labelString: group.ticker,
      absolute,
      percentage,
    };
  });

interface IndustryGroup {
  industry: string;
  balance: Amount;
}

const byIndustry = (bondHoldings: AccountBondHolding[], totalAmount: Decimal): PortfolioDistribution =>
  Array.from(
    bondHoldings
      .reduce((acc, bondHoldings) => {
        const industry = bondHoldings.pricedBond.industry;
        const item = acc.get(industry) ?? { industry, balance: asAmount(new Decimal(0)) };
        acc.set(industry, { ...item, balance: asAmount(item.balance.plus(bondHoldings.bondBalanceAccount.balance)) });

        return acc;
      }, new Map<string, IndustryGroup>())
      .values(),
  ).map((group: IndustryGroup): PortfolioPart => {
    const absolute = group.balance;
    const percentage = absolute.div(totalAmount);

    return {
      id: group.industry,
      label: group.industry,
      labelString: group.industry,
      absolute,
      percentage,
    };
  });

const groupByMapping: GroutByMap = {
  bond: byBond,
  industry: byIndustry,
  issuer: byIssuer,
  ticker: byTicker,
};

export const createPortfolioDistribution = (
  bondHoldings: AccountBondHolding[],
  totalAmount: Decimal,
  groupBy: PortfolioDistributionGroupBy,
): PortfolioDistribution => groupByMapping[groupBy](bondHoldings, totalAmount);
